/**
 * AB cart subscribe panel — expand/collapse + tier / one-time selection.
 * Toggle only shows/hides the dropdown. Option clicks change the plan.
 * Scoped to [data-cart-ab="variant"] only.
 */
(function () {
  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  let requestInFlight = false;

  /**
   * @param {string} html
   * @param {string} selector
   * @returns {string}
   */
  function getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  /**
   * @param {Record<string, string>} sections
   */
  function applyCartSections(sections) {
    if (!sections) return;

    const drawerEl = document.getElementById('CartDrawer');
    const drawerInner = drawerEl
      ? drawerEl.querySelector('.drawer__inner')
      : null;

    if (drawerInner && sections['cart-drawer']) {
      drawerInner.innerHTML =
        getSectionInnerHTML(sections['cart-drawer'], '.drawer__inner') ||
        drawerInner.innerHTML;
    }

    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (cartIconBubble && sections['cart-icon-bubble']) {
      cartIconBubble.innerHTML =
        getSectionInnerHTML(sections['cart-icon-bubble'], '.shopify-section') ||
        cartIconBubble.innerHTML;
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {boolean} isLoading
   */
  function setLoading(root, isLoading) {
    root.style.transition = 'opacity 300ms ease';
    root.style.opacity = isLoading ? '0.35' : '1';
    root.querySelectorAll('[data-cart-ab-subscribe-option]').forEach((el) => {
      if (isLoading) {
        el.setAttribute('disabled', 'disabled');
      } else {
        el.removeAttribute('disabled');
      }
    });
  }

  /**
   * @param {object} body
   * @returns {Promise<object>}
   */
  async function cartChange(body) {
    const response = await fetch(window.Shopify?.routes?.root
      ? `${window.Shopify.routes.root}cart/change.js`
      : '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...body,
        sections: SECTIONS,
        sections_url: window.location.pathname,
      }),
    });
    return response.json();
  }

  /**
   * @param {object} item
   * @returns {Promise<object>}
   */
  async function cartAdd(item) {
    const response = await fetch(window.Shopify?.routes?.root
      ? `${window.Shopify.routes.root}cart/add.js`
      : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [item],
        sections: SECTIONS,
        sections_url: window.location.pathname,
      }),
    });
    return response.json();
  }

  /**
   * @param {HTMLElement} root
   * @param {boolean} open
   */
  function setPanelOpen(root, open) {
    const panel = root.querySelector('[data-cart-ab-subscribe-panel]');
    const disclosure = root.querySelector('[data-cart-ab-subscribe-disclosure]');
    root.classList.toggle('is-open', open);
    if (disclosure) {
      disclosure.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    if (!panel) return;
    if (open) {
      panel.removeAttribute('hidden');
    } else {
      panel.setAttribute('hidden', '');
    }
  }

  /**
   * Keep dropdown open after cart HTML refresh when the user had it open.
   * @param {{ key?: string, variantId?: string }} findBy
   */
  function restorePanelOpen(findBy) {
    const scope = document.querySelector('[data-cart-ab="variant"]');
    if (!scope) return;

    let root = null;
    if (findBy.variantId) {
      root = scope.querySelector(
        `[data-cart-ab-subscribe][data-variant-id="${findBy.variantId}"]`,
      );
    }
    if (!root && findBy.key) {
      root = scope.querySelector(
        `[data-cart-ab-subscribe][data-key="${findBy.key}"]`,
      );
    }
    if (!root) return;
    setPanelOpen(root, true);
  }

  /**
   * @param {HTMLElement} root
   * @param {string} percentage
   */
  function updateBadges(root, percentage) {
    if (!percentage) return;
    const label = `SAVE ${percentage}%`;
    root.querySelectorAll('[data-cart-ab-subscribe-badge]').forEach((badge) => {
      badge.textContent = label;
    });
  }

  /**
   * @returns {string}
   */
  function cartJsUrl() {
    return window.Shopify?.routes?.root
      ? `${window.Shopify.routes.root}cart.js`
      : '/cart.js';
  }

  /**
   * DOM fallback when cart.js cannot be read. Selling plan is included only
   * when the current line is already a subscription.
   * @param {HTMLElement} root
   * @returns {object|null}
   */
  function snapshotFromRoot(root) {
    const variantId = root.dataset.variantId;
    if (!variantId) return null;

    const selectedSubscription = root.querySelector(
      '[data-cart-ab-subscribe-option][data-purchase-type="subscription"].is-selected',
    );
    const planId = selectedSubscription
      ? selectedSubscription.dataset.sellingPlanId || root.dataset.sellingPlanId
      : '';

    const snapshot = {
      variant_id: Number(variantId),
      quantity: parseInt(root.dataset.quantity, 10) || 1,
    };

    if (planId) {
      snapshot.selling_plan_allocation = {
        selling_plan: { id: Number(planId) },
      };
    }

    return snapshot;
  }

  /**
   * Read the live line so a failed swap can put the same variant, quantity,
   * selling plan, and properties back.
   * @param {string} key
   * @param {number} line
   * @returns {Promise<object|null>}
   */
  async function fetchLineSnapshot(key, line) {
    const response = await fetch(cartJsUrl(), { credentials: 'same-origin' });
    if (!response.ok) return null;

    const cart = await response.json();
    const items = Array.isArray(cart.items) ? cart.items : [];

    if (key) {
      const byKey = items.find((item) => item.key === key);
      if (byKey) return byKey;
    }

    if (line >= 1 && items[line - 1]) return items[line - 1];
    return null;
  }

  /**
   * @param {object|null|undefined} item
   * @returns {object|null}
   */
  function lineItemToAddPayload(item) {
    const variantId = item?.variant_id || item?.id;
    if (!variantId) return null;

    const payload = {
      id: Number(variantId),
      quantity: Number(item.quantity) || 1,
    };

    const planId = item.selling_plan_allocation?.selling_plan?.id;
    if (planId) payload.selling_plan = Number(planId);

    const properties = item.properties;
    if (
      properties &&
      typeof properties === 'object' &&
      Object.keys(properties).length > 0
    ) {
      payload.properties = properties;
    }

    return payload;
  }

  function publishCartUpdate() {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-ab-subscribe' });
    }
  }

  /**
   * Put the removed line back. If that also fails, show the post-remove cart
   * so the drawer matches what Shopify stored.
   * @param {HTMLElement} root
   * @param {object|null} snapshot
   * @param {Record<string, string>|undefined} fallbackSections
   * @param {boolean} keepOpen
   */
  async function rollbackRemovedLine(root, snapshot, fallbackSections, keepOpen) {
    const payload = lineItemToAddPayload(snapshot);

    try {
      if (payload) {
        const restoreResult = await cartAdd(payload);
        if (!restoreResult?.status && !restoreResult?.errors) {
          applyCartSections(restoreResult.sections || fallbackSections);
          if (keepOpen) {
            const restoredVariantId = snapshot?.variant_id || snapshot?.id;
            if (restoredVariantId) {
              restorePanelOpen({ variantId: String(restoredVariantId) });
            }
          }
          publishCartUpdate();
          return;
        }
        console.error('[cart-ab-subscribe] restore failed', restoreResult);
      }
    } catch (error) {
      console.error('[cart-ab-subscribe] restore failed', error);
    }

    applyCartSections(fallbackSections);
    setLoading(root, false);
  }

  /**
   * Swap to another pack variant: remove the current line first, then add
   * the replacement so we never leave two lines in the cart. If the add
   * fails, the original line is added back.
   * @param {HTMLElement} root
   * @param {{ variantId: string, sellingPlanId: string, quantity: number, line: number, key: string, keepOpen: boolean }} opts
   */
  async function replaceLineItem(root, opts) {
    const { variantId, sellingPlanId, quantity, line, key, keepOpen } = opts;
    setLoading(root, true);

    let removed = false;
    let added = false;
    let snapshot = snapshotFromRoot(root);
    /** @type {Record<string, string>|undefined} */
    let removeSections;

    try {
      try {
        const liveLine = await fetchLineSnapshot(key, line);
        if (liveLine) snapshot = liveLine;
      } catch (snapshotError) {
        console.warn('[cart-ab-subscribe] snapshot failed', snapshotError);
      }

      // Remove current line first (line index is stable before any add).
      const removeBody = key
        ? { id: key, quantity: 0 }
        : { line, quantity: 0 };
      const removeResult = await cartChange(removeBody);

      if (removeResult.status || removeResult.errors) {
        console.error('[cart-ab-subscribe] remove failed', removeResult);
        setLoading(root, false);
        return;
      }

      removed = true;
      removeSections = removeResult.sections;

      const addBody = {
        id: Number(variantId),
        quantity: Number(quantity) || 1,
      };
      if (sellingPlanId) {
        addBody.selling_plan = Number(sellingPlanId);
      }

      const addResult = await cartAdd(addBody);
      if (addResult.status || addResult.errors) {
        console.error('[cart-ab-subscribe] add failed', addResult);
        await rollbackRemovedLine(root, snapshot, removeSections, keepOpen);
        return;
      }

      added = true;
      applyCartSections(addResult.sections || removeSections);

      if (keepOpen) {
        restorePanelOpen({ variantId: String(variantId) });
      }

      publishCartUpdate();
    } catch (error) {
      console.error('[cart-ab-subscribe] replace failed', error);
      if (removed && !added) {
        await rollbackRemovedLine(root, snapshot, removeSections, keepOpen);
        return;
      }
      setLoading(root, false);
    }
  }

  /**
   * Update selling plan on the current line (same variant) — no add/remove.
   * @param {HTMLElement} root
   * @param {{ line: number, key: string, sellingPlanId: string, variantId: string, keepOpen: boolean }} opts
   */
  async function updateLineSellingPlan(root, opts) {
    setLoading(root, true);

    try {
      const changeBody = opts.key
        ? {
            id: opts.key,
            quantity: parseInt(root.dataset.quantity, 10) || 1,
            selling_plan: opts.sellingPlanId || '',
          }
        : {
            line: opts.line,
            selling_plan: opts.sellingPlanId || '',
          };

      const result = await cartChange(changeBody);

      if (result.status || result.errors) {
        console.error('[cart-ab-subscribe] plan update failed', result);
        setLoading(root, false);
        return;
      }

      applyCartSections(result.sections);

      if (opts.keepOpen) {
        restorePanelOpen({
          key: opts.key,
          variantId: opts.variantId,
        });
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-ab-subscribe' });
      }
    } catch (error) {
      console.error('[cart-ab-subscribe] plan update failed', error);
      setLoading(root, false);
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement} option
   */
  async function onOptionSelect(root, option) {
    if (
      requestInFlight ||
      option.classList.contains('is-selected') ||
      option.hasAttribute('disabled')
    ) {
      return;
    }

    const purchaseType = option.dataset.purchaseType;
    const variantId = String(option.dataset.variantId || '');
    const sellingPlanId = option.dataset.sellingPlanId || '';
    const savePercentage = option.dataset.savePercentage || '';
    const currentVariantId = String(root.dataset.variantId || '');
    const key = root.dataset.key || '';
    const line = parseInt(root.dataset.line, 10);
    const quantity = parseInt(root.dataset.quantity, 10) || 1;
    const keepOpen = root.classList.contains('is-open');

    requestInFlight = true;

    try {
      if (purchaseType === 'subscription' && savePercentage) {
        updateBadges(root, savePercentage);
      }

      // Same pack variant: only attach / clear selling plan (no new line).
      if (purchaseType === 'onetime' || variantId === currentVariantId) {
        await updateLineSellingPlan(root, {
          line,
          key,
          sellingPlanId: purchaseType === 'onetime' ? '' : sellingPlanId,
          variantId: currentVariantId,
          keepOpen,
        });
        return;
      }

      // Different pack size: swap the line (remove then add).
      await replaceLineItem(root, {
        variantId,
        sellingPlanId,
        quantity,
        line,
        key,
        keepOpen,
      });
    } finally {
      requestInFlight = false;
    }
  }

  /**
   * Slider toggle: on → apply subscription; off → one-time + close panel.
   * @param {HTMLElement} root
   * @param {HTMLInputElement} toggle
   */
  async function onToggleChange(root, toggle) {
    if (requestInFlight) {
      toggle.checked = !toggle.checked;
      return;
    }

    const key = root.dataset.key || '';
    const line = parseInt(root.dataset.line, 10);
    const variantId = String(root.dataset.variantId || '');
    const sellingPlanId = toggle.dataset.sellingPlanId || root.dataset.sellingPlanId || '';

    if (toggle.checked) {
      setPanelOpen(root, true);

      const preferred =
        root.querySelector(
          '[data-cart-ab-subscribe-option][data-purchase-type="subscription"][data-variant-id="' +
            variantId +
            '"]',
        ) ||
        root.querySelector(
          '[data-cart-ab-subscribe-option][data-purchase-type="subscription"]',
        );

      const planId = preferred?.dataset.sellingPlanId || sellingPlanId;
      const nextVariantId = preferred?.dataset.variantId || variantId;
      const savePercentage = preferred?.dataset.savePercentage || '';

      if (savePercentage) updateBadges(root, savePercentage);

      const isAlreadySub = Boolean(
        root.querySelector(
          '[data-cart-ab-subscribe-option][data-purchase-type="subscription"].is-selected',
        ),
      );
      if (isAlreadySub && nextVariantId === variantId) return;

      requestInFlight = true;
      try {
        if (nextVariantId && nextVariantId !== variantId) {
          await replaceLineItem(root, {
            variantId: nextVariantId,
            sellingPlanId: planId,
            quantity: parseInt(root.dataset.quantity, 10) || 1,
            line,
            key,
            keepOpen: true,
          });
          return;
        }

        await updateLineSellingPlan(root, {
          line,
          key,
          sellingPlanId: planId,
          variantId,
          keepOpen: true,
        });
      } finally {
        requestInFlight = false;
      }
      return;
    }

    setPanelOpen(root, false);

    const isAlreadyOnetime = Boolean(
      root.querySelector(
        '[data-cart-ab-subscribe-option][data-purchase-type="onetime"].is-selected',
      ),
    );
    if (isAlreadyOnetime) return;

    requestInFlight = true;
    try {
      await updateLineSellingPlan(root, {
        line,
        key,
        sellingPlanId: '',
        variantId,
        keepOpen: false,
      });
    } finally {
      requestInFlight = false;
    }
  }

  /**
   * Label text: only show / hide the dropdown.
   * @param {HTMLElement} root
   */
  function onDisclosureClick(root) {
    const isOpen = root.classList.contains('is-open');
    setPanelOpen(root, !isOpen);
  }

  document.addEventListener('change', (event) => {
    const toggle = event.target.closest('[data-cart-ab-subscribe-toggle]');
    if (!toggle) return;
    if (!toggle.closest('[data-cart-ab="variant"]')) return;

    const root = toggle.closest('[data-cart-ab-subscribe]');
    if (!root) return;
    onToggleChange(root, toggle);
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-cart-ab="variant"]')) return;

    const disclosure = event.target.closest('[data-cart-ab-subscribe-disclosure]');
    if (disclosure) {
      event.preventDefault();
      const root = disclosure.closest('[data-cart-ab-subscribe]');
      if (!root) return;
      onDisclosureClick(root);
      return;
    }

    const option = event.target.closest('[data-cart-ab-subscribe-option]');
    if (!option) return;

    event.preventDefault();
    const root = option.closest('[data-cart-ab-subscribe]');
    if (!root) return;
    onOptionSelect(root, option);
  });
})();
